/* eslint-disable import/prefer-default-export */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import { EMAIL_REGEXP } from '~/constants/global';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/**
 * Take in an email string and validate it against an email regex
 * @param  {String} email The selected email address to check
 * @return {Boolean}      If the email is valid
 */
export const isValidEmail = (email) => EMAIL_REGEXP.test(String(email).toLowerCase());

export const getCSRFCookie = () => {
  if (!process.browser) {
    return '';
  }

  const cookie = document.cookie.split(';').map((c) => ({
    key: c.split('=')[0].trim(),
    value: c.split('=')[1].trim(),
  })).find((c) => c.key === 'csrftoken');

  return cookie && cookie.value;
};

/* returns if the screen has touch points on it
    `ontouchstart` is more modern, but not supported by all browsers
    maxTouchpoints should cover the rest
*/
export const isTouchScreen = () => process.browser && (
  ('ontouchstart' in window)
  || (navigator.maxTouchPoints > 0)
  || (navigator.msMaxTouchPoints > 0)
);

export const formatToHumanReadableDate = (dateStr) => dayjs(dateStr).format('MMMM DD, YYYY');

export const humanizeDateWithTimeZone = (dateStr) => dayjs(dateStr).format('ddd DD MMM YYYY, hh:mm a z');

// generates "Website templates" from "Websites" & "Templates"
export const taxonomyTitle = (category, group) => {
  const lowerGroup = group.toLowerCase();

  if (category) {
    const words = `${category}`.split(' ').filter(Boolean);

    if (words.length > 1) {
      const lastWord = words[words.length - 1].toLowerCase().replace(/s+$/, '');
      const trimmedGroup = lowerGroup.replace(/s+$/, '');

      if (lastWord === trimmedGroup) {
        // remove duplicate trailing duplicate word
        words.pop();
      }
    }

    if (!(words[words.length - 1] === 'themes' && lowerGroup === 'templates')) {
      words[words.length - 1].replace(/s+$/, '');
      words.push(lowerGroup);
    }

    return words.join(' ');
  }

  return group;
};

export const extractBrowse = (pathMatch) => {
  if (pathMatch) {
    const [group, category, subcategory] = pathMatch.split('/').filter(Boolean);

    if (!['browse', 'search', 'collections'].includes(group.toLowerCase())) {
      return {
        group,
        category,
        subcategory,
      };
    }

    if (group.toLowerCase() === 'collections') {
      if (subcategory) {
        return {
          collection: category,
          dynamicCollection: subcategory,
        };
      }

      return {
        collection: category,
      };
    }
  }

  return {};
};

export const isWishlistCollectionSlug = (routeParams) => {
  const paramVals = extractBrowse(routeParams);
  if (!!paramVals?.collection && !!paramVals?.dynamicCollection) {
    return paramVals?.collection === 'wishlists';
  }
  return false;
};

export const isPDPPage = (routePath) => {
  if (routePath) {
    const paths = routePath.split('/');
    return paths.find((p) => p === 'products') && (paths.length === 2);
  }

  return false;
};

/**
 * Sanitizes string of newlines, caps length, and trims whitespace
 * @param  {String} str to sanitize/trim
 * @param  {Integer} maxLength of returned string
 * @return {String} of maxLength with newlines (\r, \n) replaced with single space (' ')
 */
export const sanitizeString = (str, maxLength = 100) => {
  const newlinesRemoved = str.replace(/\n+|\r?\n/g, ' ');

  if (newlinesRemoved.length > maxLength) {
    return newlinesRemoved.substring(0, maxLength).trim();
  }

  return newlinesRemoved.trim();
};

export const firstSentence = (str, maxLength = 60, minLength = 8) => {
  if (str.length <= minLength) {
    return str;
  }

  // find first sentence with a minimum of minLength chars
  for (let i = Math.min(str.length, minLength); i < Math.min(str.length, maxLength); i += 1) {
    const c = str.charAt(i);

    if (['?', '.', '!'].includes(c)) {
      return str.substr(0, i + 1);
    }
  }

  // no separator found, return full string
  if (str.length < maxLength) {
    return str;
  }

  // no sentence found, stop at last word prior to maxLength
  const i = str.lastIndexOf(' ', maxLength);
  if (i > 0) {
    return str.substr(0, i);
  }

  // last resort just trim maxLength
  return str.substr(0, maxLength);
};

export const lastWordAt = (str, maxLength = 160) => {
  if (str.length > maxLength) {
    const i = str.lastIndexOf(' ', maxLength);
    if (i > 0) {
      return str.substr(0, i);
    }
  }

  return str.substr(0, maxLength);
};

const sanitizeColor = (color) => (Math.min(255, Math.max(0, color)));

export const lightenColor = (color, percent) => {
  const num = parseInt(color, 16);
  const amt = Math.round(2.55 * percent);
  const R = sanitizeColor((num >> 16) + amt); // eslint-disable-line no-bitwise
  const B = sanitizeColor(((num >> 8) & 0x00FF) + amt); // eslint-disable-line no-bitwise
  const G = sanitizeColor((num & 0x0000FF) + amt); // eslint-disable-line no-bitwise

  return (0x1000000 + R * 0x10000 + B * 0x100 + G).toString(16).slice(1);
};

export const setUserDataLayer = (user, $gtm) => {
  const dataLayerData = {};

  if (user) {
    dataLayerData.userId = user.id;
    dataLayerData.userEmail = user.email;
    dataLayerData.userEmailHash = user.email_hash;

    if (user.has_plus) {
      dataLayerData.hasPlus = 'True';
    }

    if (user.has_briefcase) {
      dataLayerData.hasBriefcase = 'True';
    }

    if (user.is_staff) {
      dataLayerData.isStaff = 'True';
    }

    if (user.is_betaling) {
      dataLayerData.isBetaling = 'True';
    }
  }

  $gtm.push(dataLayerData);
};

/**
 * Get UTM params for social share modal.
 * @param {Object} data - Deal or wishlist data
 * @param {String} data.slug - Deal or wishlist slug
 * @param {Number} data.id - Deal or wishlist id
 * @param {Number} userId - Id of the logged in user
 * @returns {Object} An object with the social share utm params
 */
export const getUtmParamsForSocialShare = (data, userId) => {
  const utmMedium = 'utm_medium=social';
  const getUtmSource = (social) => `utm_source=${data.slug}-share-${social}`;
  const getUtmCampaign = (social) => {
    const userIdString = userId ? `-${userId}` : '';
    return `utm_campaign=${data.slug}-share-${social}-${data.id}${userIdString}`;
  };

  return {
    text: `?${getUtmSource('link')}&${utmMedium}&${getUtmCampaign('link')}`,
    email: `?${getUtmSource('email')}&${utmMedium}&${getUtmCampaign('email')}`,
    facebook: `?${getUtmSource('facebook')}&${utmMedium}&${getUtmCampaign('facebook')}`,
    linkedIn: `?${getUtmSource('linkedin')}&${utmMedium}&${getUtmCampaign('linkedin')}`,
    twitter: `?${getUtmSource('twitter')}&${utmMedium}&${getUtmCampaign('twitter')}`,
  };
};

export const getPlanInterval = (planTypes) => {
  // If the deal has multiple plan types, we'll just use the first one.
  // If the deal has different intervals for each plan type, we'll have to revisit this.

  // planTuple = [planType, planInterval, intervalCount]
  const planTuple = planTypes?.[0] || [];

  return planTuple[2];
};
